import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type {
  ChangeProPlan,
  CreateProCheckoutSession,
  LoadMyEntitlements,
  LoadProPlanCatalog,
  LoadProSubscription,
  PreviewProPlanChange,
  SetSession,
  ValidateProPromoCode,
} from '@/modules/auth/use-cases'
import type {
  EmitFunnelEvent,
  MyEntitlements,
  ProPlanCatalog,
  ProPlanChangePreview,
  ProPromoCodeValidation,
  ProSubscriptionSummary,
} from '@/shared/domain'
import {
  PlanChangeAmountMismatchError,
  proAnnualSavingsLabel,
  proPlanPrice,
  proPlanPriceLabel,
  UserFacingError,
} from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import { isInFlutterWebview, isIosDevice } from '@/shared/ui/hooks'
import {
  hasOrgGrantedProAccess,
  selectProPaywallPanel,
  upgradeBlockedReason,
  WU_BUNDLE_PLAN,
} from '../select-pro-paywall-panel'
import { PRO_UPGRADE_PLAN_LABEL, selectProUpgradeCharge } from '../select-pro-upgrade-charge'
import type { ProBillingPeriod, ProPaywallPanel, ProPlanChoice } from '../types'

export type UseProPaywallPageProps = {
  onCreateProCheckoutSession: CreateProCheckoutSession
  onValidateProPromoCode: ValidateProPromoCode
  onLoadProSubscription: LoadProSubscription
  onLoadProEntitlements: LoadMyEntitlements
  onLoadProPlanCatalog: LoadProPlanCatalog
  onPreviewProPlanChange: PreviewProPlanChange
  onChangeProPlan: ChangeProPlan
  onSetSession: SetSession
  onEmitFunnelEvent: EmitFunnelEvent
  deepLinkScheme: string | null
}

export type ProUpgradeConfirmView = {
  isOpen: boolean
  title: string
  description: string
  details: string[]
  confirmLabel: string
  isBusy: boolean
  errorMessage: string
}

export type ProUpgradeView = {
  isLoading: boolean
  isTrial: boolean
  planLabel: string
  planPriceLabel: string
  creditLabel: string
  amountDueNowLabel: string
  renewalAmountLabel: string
  renewalDateLabel: string
  cardLabel: string
  periodResetLine: string
  canConfirm: boolean
  isConfirming: boolean
  blockedReason: string | null
  error: string | null
  confirm: ProUpgradeConfirmView
}

export type ProManageInStoreView = {
  url: string
  linkLabel: string
  description: string
}

export type UseProPaywallPageResult = {
  ready: boolean
  panel: ProPaywallPanel | null
  isPanelLoading: boolean
  planChoice: ProPlanChoice
  billingPeriod: ProBillingPeriod
  promoCode: string
  promoApplied: boolean
  appliedPromo: ProPromoCodeValidation | null
  isApplyingPromo: boolean
  promoError: string | null
  showInAppOption: boolean
  showPlanForm: boolean
  showOrgGrantNotice: boolean
  showProFeatures: boolean
  showWuFeatures: boolean
  isRedirecting: boolean
  error: string | null
  proPriceLabel: string
  monthlyPriceLabel: string
  annualPriceLabel: string
  annualSavingsLabel: string
  wuPriceLabel: string
  ctaLabel: string
  showRestoreOption: boolean
  showNotNow: boolean
  showNotNowState: boolean
  notNowInApp: boolean
  upgrade: ProUpgradeView
  manageInStore: ProManageInStoreView
  setPlanChoice: (plan: ProPlanChoice) => void
  setBillingPeriod: (period: ProBillingPeriod) => void
  setPromoCode: (code: string) => void
  handleShowProFeatures: () => void
  handleApplyPromo: () => Promise<void>
  handleContinue: () => Promise<void>
  handleInAppPurchase: () => void
  handleRestore: () => void
  handleReviewUpgrade: () => void
  handleUpgradeDialogOpenChange: (open: boolean) => void
  handleConfirmUpgrade: () => Promise<void>
  handleNotNow: () => void
}

const APP_STORE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions'
const PLAY_STORE_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions'

const PENDING_UPGRADE_MESSAGE =
  'Your bank needs to confirm this payment before the upgrade completes. Try again in a moment.'

const DEFAULT_PAYWALL_SOURCE = 'web_pro_page'
const PRO_PURCHASE_COMPLETED_EMITTED_KEY = 'bb_pro_purchase_completed_emitted'

type IapPlan = 'monthly' | 'annual' | 'wuAnnual'

export function useProPaywallPage({
  onCreateProCheckoutSession,
  onValidateProPromoCode,
  onLoadProSubscription,
  onLoadProEntitlements,
  onLoadProPlanCatalog,
  onPreviewProPlanChange,
  onChangeProPlan,
  onSetSession,
  onEmitFunnelEvent,
  deepLinkScheme,
}: UseProPaywallPageProps): UseProPaywallPageResult {
  const [searchParams] = useSearchParams()
  const [planChoice, setPlanChoice] = useState<ProPlanChoice>(() =>
    searchParams.get('plan') === 'wu' ? 'wu' : 'pro',
  )
  const [billingPeriod, setBillingPeriod] = useState<ProBillingPeriod>('annual')
  const [promoCode, setPromoCodeState] = useState<string>('')
  const [appliedPromo, setAppliedPromo] = useState<ProPromoCodeValidation | null>(null)
  const [appliedPromoCode, setAppliedPromoCode] = useState<string>('')
  const [isApplyingPromo, setIsApplyingPromo] = useState<boolean>(false)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [ready, setReady] = useState<boolean>(false)
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [subscription, setSubscription] = useState<ProSubscriptionSummary | null>(null)
  const [entitlements, setEntitlements] = useState<MyEntitlements | null>(null)
  const [catalog, setCatalog] = useState<ProPlanCatalog | null>(null)
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(true)
  const [isEntitlementsLoading, setIsEntitlementsLoading] = useState<boolean>(true)
  const [reloadToken, setReloadToken] = useState<number>(0)
  const [preview, setPreview] = useState<ProPlanChangePreview | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false)
  const [upgradeError, setUpgradeError] = useState<string | null>(null)
  const [isConfirmingUpgrade, setIsConfirmingUpgrade] = useState<boolean>(false)
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState<boolean>(false)
  const [upgradeConfirmError, setUpgradeConfirmError] = useState<string>('')
  const [previewToken, setPreviewToken] = useState<number>(0)
  const [skippedForNow, setSkippedForNow] = useState<boolean>(false)
  const bootstrapRef = useRef<boolean>(false)
  const firedPaywallViewedRef = useRef<boolean>(false)
  const firedPurchaseCompletedRef = useRef<boolean>(false)

  const source = searchParams.get('source')?.trim() || DEFAULT_PAYWALL_SOURCE
  const openedFromApp = searchParams.get('app') === 'mobile'
  const appSuffix = openedFromApp ? '&app=mobile' : ''

  useEffect(() => {
    if (bootstrapRef.current) return
    const accessToken = searchParams.get('access_token')
    const refreshToken = searchParams.get('refresh_token')
    if (!accessToken || !refreshToken) {
      setReady(true)
      return
    }
    bootstrapRef.current = true
    if (typeof window !== 'undefined') {
      const preserved = new URLSearchParams(searchParams)
      preserved.delete('access_token')
      preserved.delete('refresh_token')
      const query = preserved.toString()
      window.history.replaceState({}, '', query === '' ? '/pro' : `/pro?${query}`)
    }
    onSetSession({ accessToken, refreshToken })
      .catch((err) => {
        reportError(err, { fallbackMessage: 'Could not restore your session.' })
      })
      .finally(() => {
        setReady(true)
      })
  }, [searchParams, onSetSession])

  useEffect(() => {
    if (!ready) return
    if (firedPaywallViewedRef.current) return
    if (isInFlutterWebview()) return
    firedPaywallViewedRef.current = true
    onEmitFunnelEvent({ eventType: 'paywall_viewed', source, plan: planChoice })
  }, [ready, source, planChoice, onEmitFunnelEvent])

  useEffect(() => {
    if (!ready) return
    if (searchParams.get('proReturn') !== '1') return
    if (firedPurchaseCompletedRef.current) return
    firedPurchaseCompletedRef.current = true
    if (typeof window !== 'undefined') {
      if (window.sessionStorage.getItem(PRO_PURCHASE_COMPLETED_EMITTED_KEY) === '1') return
      window.sessionStorage.setItem(PRO_PURCHASE_COMPLETED_EMITTED_KEY, '1')
    }
    onEmitFunnelEvent({
      eventType: 'pro_purchase_completed',
      source,
      plan: planChoice,
      rail: 'stripe',
    })
  }, [ready, searchParams, source, planChoice, onEmitFunnelEvent])

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    setIsSubscriptionLoading(true)
    onLoadProSubscription()
      .then((summary) => {
        if (!cancelled) setSubscription(summary)
      })
      .catch(() => {
        if (!cancelled) setSubscription(null)
      })
      .finally(() => {
        if (!cancelled) setIsSubscriptionLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ready, reloadToken, onLoadProSubscription])

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    setIsEntitlementsLoading(true)
    onLoadProEntitlements()
      .then((summary) => {
        if (!cancelled) setEntitlements(summary)
      })
      .catch(() => {
        if (!cancelled) setEntitlements(null)
      })
      .finally(() => {
        if (!cancelled) setIsEntitlementsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ready, reloadToken, onLoadProEntitlements])

  useEffect(() => {
    let cancelled = false
    onLoadProPlanCatalog()
      .then((result) => {
        if (!cancelled) setCatalog(result)
      })
      .catch(() => {
        if (!cancelled) setCatalog(null)
      })
    return () => {
      cancelled = true
    }
  }, [onLoadProPlanCatalog])

  const monthlyPriceLabel = useMemo(
    () => proPlanPriceLabel(proPlanPrice(catalog, 'bb_pro_monthly')),
    [catalog],
  )
  const annualPriceLabel = useMemo(
    () => proPlanPriceLabel(proPlanPrice(catalog, 'bb_pro_annual')),
    [catalog],
  )
  const wuPriceLabel = useMemo(
    () => proPlanPriceLabel(proPlanPrice(catalog, WU_BUNDLE_PLAN)),
    [catalog],
  )
  const annualSavingsLabel = useMemo(
    () =>
      proAnnualSavingsLabel(
        proPlanPrice(catalog, 'bb_pro_monthly'),
        proPlanPrice(catalog, 'bb_pro_annual'),
      ),
    [catalog],
  )

  const isPanelDataLoading = isSubscriptionLoading || isEntitlementsLoading

  const panel = useMemo(() => selectProPaywallPanel(subscription), [subscription])
  const blockedReason = useMemo(() => upgradeBlockedReason(subscription), [subscription])

  const shouldLoadPreview =
    panel === 'stripe-upgrade' && blockedReason === null && !isPanelDataLoading

  useEffect(() => {
    if (!shouldLoadPreview) return
    let cancelled = false
    setIsPreviewLoading(true)
    onPreviewProPlanChange({ targetPlan: WU_BUNDLE_PLAN })
      .then((quote) => {
        if (!cancelled) setPreview(quote)
      })
      .catch((err) => {
        if (cancelled) return
        setUpgradeError(
          reportError(err, {
            fallbackMessage: 'Could not price this upgrade. Please try again.',
          }),
        )
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [shouldLoadPreview, previewToken, onPreviewProPlanChange])

  const handleShowProFeatures = useCallback(() => {
    if (typeof document === 'undefined') return
    document.getElementById('pro-feature-list')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const setPromoCode = useCallback((value: string) => {
    setPromoCodeState(value.toUpperCase())
    setAppliedPromo(null)
    setAppliedPromoCode('')
    setPromoError(null)
  }, [])

  const promoApplied = appliedPromo !== null && appliedPromoCode === promoCode && promoCode !== ''

  const handleApplyPromo = useCallback(async () => {
    const code = promoCode.trim()
    if (code === '') return
    setPromoError(null)
    setIsApplyingPromo(true)
    try {
      const result = await onValidateProPromoCode({ code })
      if (!result.valid) {
        setAppliedPromo(null)
        setAppliedPromoCode('')
        setPromoError('This promo code is not valid.')
        return
      }
      setAppliedPromo(result)
      setAppliedPromoCode(promoCode)
    } catch {
      setAppliedPromo(null)
      setAppliedPromoCode('')
      setPromoError('Could not validate the promo code. Please try again.')
    } finally {
      setIsApplyingPromo(false)
    }
  }, [promoCode, onValidateProPromoCode])

  const handleContinue = useCallback(async () => {
    setIsRedirecting(true)
    setError(null)
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const trimmedPromoCode = promoCode.trim()
      const result = await onCreateProCheckoutSession({
        plan:
          planChoice === 'wu'
            ? WU_BUNDLE_PLAN
            : billingPeriod === 'monthly'
              ? 'bb_pro_monthly'
              : 'bb_pro_annual',
        successUrl: `${origin}/pro?proReturn=1&source=${encodeURIComponent(source)}${appSuffix}`,
        cancelUrl: `${origin}/pro?source=${encodeURIComponent(source)}${appSuffix}`,
        ...(trimmedPromoCode ? { promoCode: trimmedPromoCode } : {}),
        source,
      })
      if (!result.url) {
        setError('Failed to create checkout session')
        setIsRedirecting(false)
        return
      }
      onEmitFunnelEvent({ eventType: 'registration_step_completed', step: 'proPaywall', source })
      if (typeof window !== 'undefined') {
        window.location.href = result.url
      }
    } catch (err) {
      setError(
        err instanceof UserFacingError ? err.message : 'Something went wrong. Please try again.',
      )
      setIsRedirecting(false)
    }
  }, [planChoice, billingPeriod, promoCode, source, appSuffix, onCreateProCheckoutSession, onEmitFunnelEvent])

  const handleInAppPurchase = useCallback(() => {
    const iapPlan: IapPlan = planChoice === 'wu' ? 'wuAnnual' : billingPeriod
    if (isInFlutterWebview()) {
      window.flutter_inappwebview?.callHandler('startIap', { plan: iapPlan }).catch(() => {})
      return
    }
    if (deepLinkScheme && typeof window !== 'undefined') {
      window.location.href = `${deepLinkScheme}://pro`
    }
  }, [planChoice, billingPeriod, deepLinkScheme])

  const handleRestore = useCallback(() => {
    if (isInFlutterWebview()) {
      window.flutter_inappwebview?.callHandler('restorePurchases').catch(() => {})
    }
  }, [])

  const handleReviewUpgrade = useCallback(() => {
    if (preview === null) return
    setUpgradeError(null)
    setUpgradeConfirmError('')
    setIsUpgradeDialogOpen(true)
  }, [preview])

  const handleUpgradeDialogOpenChange = useCallback(
    (open: boolean) => {
      if (isConfirmingUpgrade) return
      setIsUpgradeDialogOpen(open)
      if (!open) setUpgradeConfirmError('')
    },
    [isConfirmingUpgrade],
  )

  const handleConfirmUpgrade = useCallback(async () => {
    if (preview === null || isConfirmingUpgrade) return
    setIsConfirmingUpgrade(true)
    setUpgradeError(null)
    setUpgradeConfirmError('')
    try {
      const result = await onChangeProPlan({
        targetPlan: WU_BUNDLE_PLAN,
        prorationDate: preview.prorationDate,
        expectedAmountDueNow: preview.amountDueNow,
        source,
      })
      setIsUpgradeDialogOpen(false)
      if (!result.applied) {
        setUpgradeError(PENDING_UPGRADE_MESSAGE)
        return
      }
      setPreview(null)
      setReloadToken((token) => token + 1)
    } catch (err) {
      if (err instanceof PlanChangeAmountMismatchError) {
        setUpgradeConfirmError(err.message)
        setPreviewToken((token) => token + 1)
        return
      }
      setIsUpgradeDialogOpen(false)
      setUpgradeError(
        reportError(err, {
          fallbackMessage: 'Could not complete the upgrade. Please try again.',
        }),
      )
    } finally {
      setIsConfirmingUpgrade(false)
    }
  }, [preview, isConfirmingUpgrade, source, onChangeProPlan])

  const upgrade = useMemo<ProUpgradeView>(() => {
    const isTrial = subscription?.status === 'trialing'
    const charge = selectProUpgradeCharge({ preview, subscription, isTrial })
    return {
      isLoading: isPreviewLoading || (preview === null && upgradeError === null),
      isTrial,
      planLabel: PRO_UPGRADE_PLAN_LABEL,
      planPriceLabel: charge.planPriceLabel,
      creditLabel: charge.creditLabel,
      amountDueNowLabel: charge.amountDueNowLabel,
      renewalAmountLabel: charge.renewalAmountLabel,
      renewalDateLabel: charge.renewalDateLabel,
      cardLabel: charge.cardLabel,
      periodResetLine: charge.periodResetLine,
      canConfirm: preview !== null,
      isConfirming: isConfirmingUpgrade,
      blockedReason,
      error: upgradeError,
      confirm: {
        isOpen: isUpgradeDialogOpen,
        title: 'Add Waterfowl U?',
        description: charge.confirmDescription,
        details: charge.confirmDetails,
        confirmLabel: isTrial ? 'Add Waterfowl U' : 'Confirm and pay',
        isBusy: isConfirmingUpgrade || isPreviewLoading,
        errorMessage: upgradeConfirmError,
      },
    }
  }, [
    preview,
    isPreviewLoading,
    upgradeError,
    isConfirmingUpgrade,
    blockedReason,
    subscription,
    isUpgradeDialogOpen,
    upgradeConfirmError,
  ])

  const manageInStore = useMemo<ProManageInStoreView>(() => {
    const onIos = isIosDevice()
    return {
      url: onIos ? APP_STORE_SUBSCRIPTIONS_URL : PLAY_STORE_SUBSCRIPTIONS_URL,
      linkLabel: onIos ? 'Manage in App Store' : 'Manage in Play Store',
      description: onIos
        ? 'Your BlindBook Pro subscription is billed through your Apple ID, so the Waterfowl U bundle has to be selected there. Open your App Store subscription settings and change your BlindBook plan.'
        : 'Your BlindBook Pro subscription is billed through Google Play, so the Waterfowl U bundle has to be selected there. Open your Play Store subscription settings and change your BlindBook plan.',
    }
  }, [])

  const selectedPriceLabel =
    planChoice === 'wu'
      ? wuPriceLabel
      : billingPeriod === 'monthly'
        ? monthlyPriceLabel
        : annualPriceLabel

  const handleNotNow = useCallback(() => {
    setSkippedForNow(true)
  }, [])

  const isUpsellPanel =
    !isPanelDataLoading && (panel === 'stripe-upgrade' || panel === 'iap-managed')

  return useMemo(
    () => ({
      ready,
      panel: isPanelDataLoading ? null : panel,
      isPanelLoading: isPanelDataLoading,
      planChoice,
      billingPeriod,
      promoCode,
      promoApplied,
      appliedPromo,
      isApplyingPromo,
      promoError,
      showInAppOption:
        !isPanelDataLoading &&
        panel === 'buy' &&
        (Boolean(deepLinkScheme) || isInFlutterWebview()),
      showPlanForm: !isPanelDataLoading && panel === 'buy',
      showOrgGrantNotice:
        !isPanelDataLoading &&
        panel === 'buy' &&
        hasOrgGrantedProAccess(entitlements) &&
        !isInFlutterWebview(),
      showProFeatures: !isPanelDataLoading && panel === 'buy',
      showWuFeatures:
        !isPanelDataLoading &&
        (panel === 'stripe-upgrade' ||
          panel === 'iap-managed' ||
          (panel === 'buy' && planChoice === 'wu')),
      isRedirecting,
      error,
      proPriceLabel: billingPeriod === 'monthly' ? monthlyPriceLabel : annualPriceLabel,
      monthlyPriceLabel,
      annualPriceLabel,
      annualSavingsLabel,
      wuPriceLabel,
      ctaLabel: selectedPriceLabel === '' ? 'Continue' : `Continue — ${selectedPriceLabel}`,
      showRestoreOption:
        !isPanelDataLoading &&
        (panel === 'buy' || panel === 'iap-managed') &&
        isInFlutterWebview(),
      showNotNow: isUpsellPanel && !skippedForNow,
      showNotNowState: skippedForNow,
      notNowInApp: openedFromApp || isInFlutterWebview(),
      upgrade,
      manageInStore,
      setPlanChoice,
      setBillingPeriod,
      setPromoCode,
      handleShowProFeatures,
      handleApplyPromo,
      handleContinue,
      handleInAppPurchase,
      handleRestore,
      handleReviewUpgrade,
      handleUpgradeDialogOpenChange,
      handleConfirmUpgrade,
      handleNotNow,
    }),
    [
      ready,
      panel,
      entitlements,
      isPanelDataLoading,
      planChoice,
      billingPeriod,
      promoCode,
      promoApplied,
      appliedPromo,
      isApplyingPromo,
      promoError,
      deepLinkScheme,
      isRedirecting,
      error,
      monthlyPriceLabel,
      annualPriceLabel,
      annualSavingsLabel,
      wuPriceLabel,
      selectedPriceLabel,
      upgrade,
      manageInStore,
      setPromoCode,
      handleShowProFeatures,
      handleApplyPromo,
      handleContinue,
      handleInAppPurchase,
      handleRestore,
      handleReviewUpgrade,
      handleUpgradeDialogOpenChange,
      handleConfirmUpgrade,
      isUpsellPanel,
      skippedForNow,
      openedFromApp,
      handleNotNow,
    ],
  )
}
